import { useId } from "react";
import { amountedExtraOptionCodes } from "features/option-controls/extra-option-controls/extras";
import { useExtraHelper } from "features/option-controls/helpers/useExtraHelper";
import { motion } from "framer-motion";
import { OptionTemplate } from "shared/api/middleware-api-client";
import Icon, { IconTypes } from "shared/ui/icon";
import { SlidesLayout } from "./SlidesLayout";
import { useOptionModalSetter } from "../MobileConfiguratorModalTemplate";
import Text from "shared/ui/typography/Text";
import Checkbox, { CheckboxVariant } from "shared/ui/checkbox";

export const ExtrasSelector = () => {
  const idPrefix = useId();
  const {
    extraOptions,
    onExtraChange,
    isExtraChecked,
    handleAmountChange,
    selectedExtras,
    extra,
  } = useExtraHelper();

  const setOptionModalState = useOptionModalSetter();

  const onInfoClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    option: OptionTemplate,
  ) => {
    e.stopPropagation();

    setOptionModalState({
      isOpen: true,
      heading: option.name,
      description: option.description,
    });
  };

  // Show extra options
  return (
    <SlidesLayout>
      <fieldset>
        <legend className="sr-only">Extra Options</legend>
        <ul className="gg-space-y-2.5">
          {extraOptions?.map((option: OptionTemplate) => {
            const isExtraFieldChecked = isExtraChecked(extra, option);
            const showAmountInputField =
              amountedExtraOptionCodes.includes(option.code) &&
              isExtraFieldChecked;

            // Find the current amount from the extra array
            const currentExtra = selectedExtras.find((e) => e.id === option.id);
            const amountValue = currentExtra?.amount ?? 1;

            // Calculated price for extra
            const extraPrice = (amountValue * option.price).toFixed(2);
            return (
              <li
                className="gg-relative gg-rounded-sm gg-border gg-border-[#D4D4DA] hover:gg-bg-grey-500"
                key={`extra-${option.id}`}
              >
                <label
                  htmlFor={`${idPrefix}-extra-${option.id}`}
                  className="gg-absolute gg-inset-0 gg-cursor-pointer"
                />
                <div className="gg-flex gg-w-full gg-items-center gg-py-3 gg-pl-1.5 gg-pr-3">
                  <div className="gg-flex gg-w-full gg-items-center gg-justify-between">
                    <div className="gg-flex gg-items-center gg-space-x-2.5">
                      <Checkbox
                        id={`${idPrefix}-extra-${option.id}`}
                        aria-label={option.name}
                        className="gg-shrink-0"
                        checked={isExtraFieldChecked}
                        onChange={() => onExtraChange(option)}
                        variant={CheckboxVariant.ROUND}
                      />
                      <div className="gg-flex gg-flex-col gg-items-start gg-gap-2">
                        <span className="gg-max-w-46 gg-text-xs gg-text-black gg-transition gg-duration-300 gg-ease-in-out">
                          {option.name}
                        </span>
                        <motion.input
                          initial={false}
                          animate={{
                            opacity: showAmountInputField ? 1 : 0,
                            scale: showAmountInputField ? 1 : 0.995,
                          }}
                          key="box"
                          type="number"
                          id={`${idPrefix}-quantity-${option.id}`}
                          aria-label={`Aantal ${option.name}`}
                          name={"extra-" + option.id}
                          disabled={!showAmountInputField}
                          min="1"
                          max="100"
                          value={amountValue}
                          onChange={(e) =>
                            handleAmountChange(option, Number(e.target.value))
                          }
                          className={` ${!showAmountInputField ? "gg-pointer-events-none" : ""} gg-relative gg-z-10 gg-h-[16px] gg-w-8 gg-border gg-border-dark-blue-200 gg-bg-gray-100 gg-pl-1.5 gg-text-xs gg-font-bold gg-text-dark-blue-500`}
                        />
                      </div>
                    </div>
                    <div className="gg-flex gg-flex-col gg-items-center">
                      {option.description !== "" && (
                        <button
                          onClick={(e) => onInfoClick(e, option)}
                          data-testid="option-info-button"
                          aria-label={`Meer informatie over ${option.name}`}
                          className="gg-group gg-relative gg-z-10 gg-shrink-0"
                        >
                          <Icon
                            name={IconTypes.INFO}
                            alt=""
                            className="gg-ml-2 gg-transition gg-duration-300 gg-ease-in-out group-hover:gg-scale-105"
                          />
                        </button>
                      )}

                      <Text
                        aria-live="polite"
                        data-testid="extra-option-formatted-price"
                        className="gg-whitespace-nowrap gg-text-xs gg-text-secondary-text"
                      >
                        {`+€${extraPrice}`}
                      </Text>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </fieldset>

      {/* <div className="gg-mt-5">
        <MobileConfiguratorGewoonZekerBlock />
      </div> */}

      {/* <MobileConfiguratorWarranties /> */}
    </SlidesLayout>
  );
};
